import { z } from "zod"
import { renderCameraSchema } from "./render.schema"

// ── Le parcours de la vidéo de présentation (Project.videoPathJson) ─────────
// Specs techniques VID, « La validation ». Un plan est un mouvement de caméra d'une durée donnée ; le parcours
// les enchaîne, et ses réglages valent pour toute la vidéo.

/** Durée d'un plan, en secondes */
export const SHOT_MIN_SECONDS = 2
export const SHOT_MAX_SECONDS = 20
/** Nombre de plans au plus dans un parcours */
export const MAX_SHOTS = 60

export const videoFormatSchema = z.enum(["169", "916", "11"])
export type VideoFormat = z.infer<typeof videoFormatSchema>

export const videoSettingsSchema = z.object({
  format: videoFormatSchema,
  title: z.string().max(80),
  subtitle: z.string().max(120),
  /** Nom et surface des pièces en légende */
  captions: z.boolean(),
  /** Fondu au noir entre deux plans */
  fades: z.boolean(),
})
export type VideoSettings = z.infer<typeof videoSettingsSchema>

/** Champs communs à tous les plans : identifiant, retenu ou écarté, durée */
const base = {
  id: z.string().min(1).max(80),
  on: z.boolean(),
  dur: z.number().min(SHOT_MIN_SECONDS).max(SHOT_MAX_SECONDS),
}

export const roomMoveSchema = z.enum(["pano", "traversee", "tour", "fixe"])
export type RoomMove = z.infer<typeof roomMoveSchema>

export const videoShotSchema = z.discriminatedUnion("type", [
  z.object({
    ...base,
    type: z.literal("tour"),
    height: z.enum(["homme", "haut", "oiseau"]),
    /** Demi-tour côté jardin */
    half: z.boolean(),
    clockwise: z.boolean(),
  }),
  z.object({ ...base, type: z.literal("survol") }),
  z.object({ ...base, type: z.literal("approche") }),
  z.object({ ...base, type: z.literal("piece"), spaceId: z.string().min(1), move: roomMoveSchema }),
  z.object({ ...base, type: z.literal("escalier"), staircaseId: z.string().min(1) }),
  z.object({
    ...base,
    type: z.literal("vue3d"),
    camera: renderCameraSchema.unwrap(),
    move: z.enum(["zoom", "glisse", "fixe"]),
  }),
  z.object({
    ...base,
    type: z.literal("carton"),
    text: z.string().max(80),
    subtitle: z.string().max(120),
    background: z.enum(["aerien", "jardin", "uni"]),
    /** Carton d'ouverture ou de fin du parcours proposé : ses textes suivent les réglages de la vidéo */
    role: z.enum(["ouverture", "fin"]).optional(),
  }),
])
export type VideoShot = z.infer<typeof videoShotSchema>
export type VideoShotType = VideoShot["type"]

export const videoPathSchema = z.object({
  version: z.literal(1),
  settings: videoSettingsSchema,
  shots: z.array(videoShotSchema).max(MAX_SHOTS),
  /** Les pièces que le parcours connaît déjà (proposées, ajoutées ou ignorées) : une autre est « apparue » */
  known: z.array(z.string().min(1)).max(500).optional(),
})
export type VideoPath = z.infer<typeof videoPathSchema>

// ── Les demandes de vidéo (VID-E04) ─────────────────────────

/** Cadence de la vidéo */
export const VIDEO_FPS = 25
/** Au plus 3 min pour un aperçu (Q2) ; 30 s pour une version réaliste (décision de Boris du 2026-10-31, après la
 *  mesure de Blender : 1 min 30 demandait des dizaines d'heures de calcul) */
export const PREVIEW_MAX_FRAMES = 3 * 60 * VIDEO_FPS
export const REALISTIC_MAX_FRAMES = 30 * VIDEO_FPS
/** Version réaliste : Blender calcule une image sur RENDER_STEP, ffmpeg crée les autres (décision du 2026-10-31) */
export const RENDER_STEP = 2

/** Définition de chaque format : l'aperçu (et la version réaliste standard), puis la haute qualité */
export const VIDEO_SIZES = {
  standard: { "169": { width: 1280, height: 720 }, "916": { width: 720, height: 1280 }, "11": { width: 720, height: 720 } },
  haute: { "169": { width: 1920, height: 1080 }, "916": { width: 1080, height: 1920 }, "11": { width: 1080, height: 1080 } },
} as const

const finite = z.number().finite()

/** La piste caméra : [px, py, pz, tx, ty, tz, fov] par image, au plus 3 min */
export const cameraTrackSchema = z.object({
  fps: z.literal(VIDEO_FPS),
  frames: z.array(z.tuple([finite, finite, finite, finite, finite, finite, z.number().min(1).max(170)])).min(1).max(PREVIEW_MAX_FRAMES),
})
export type CameraTrackInput = z.infer<typeof cameraTrackSchema>

const frameNo = z.number().int().min(0)
/** Le plan de finition, en numéros d'image */
export const finishPlanSchema = z.object({
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  cards: z.array(z.object({
    from: frameNo, to: frameNo, text: z.string().max(80), subtitle: z.string().max(120), background: z.enum(["aerien", "jardin", "uni"]),
  })).max(MAX_SHOTS),
  captions: z.array(z.object({ from: frameNo, to: frameNo, text: z.string().max(200) })).max(MAX_SHOTS),
  fades: z.array(z.object({ at: frameNo })).max(MAX_SHOTS),
})
export type FinishPlanInput = z.infer<typeof finishPlanSchema>

export const videoKindSchema = z.enum(["apercu", "realiste"])
export type VideoKind = z.infer<typeof videoKindSchema>
export const videoQualitySchema = z.enum(["standard", "haute"])
export type VideoQuality = z.infer<typeof videoQualitySchema>

/** Une demande de vidéo, une fois ses champs lus (multipart) ; les refus portent le message montré */
export const createVideoSchema = z
  .object({
    kind: videoKindSchema,
    quality: videoQualitySchema.optional(),
    format: videoFormatSchema,
    frameCount: z.number().int().min(1),
    track: cameraTrackSchema,
    finish: finishPlanSchema,
  })
  .superRefine((v, ctx) => {
    if (v.kind === "apercu" && v.frameCount > PREVIEW_MAX_FRAMES) {
      ctx.addIssue({ code: "custom", message: "Un aperçu dure 3 min au plus" })
    }
    if (v.kind === "realiste" && v.frameCount > REALISTIC_MAX_FRAMES) {
      ctx.addIssue({ code: "custom", message: "La version réaliste dure 30 s au plus" })
    }
    if (v.frameCount !== v.track.frames.length) {
      ctx.addIssue({ code: "custom", message: "La piste ne correspond pas à la durée de la vidéo" })
    }
    const size = VIDEO_SIZES[v.kind === "realiste" ? v.quality ?? "standard" : "standard"][v.format]
    if (v.finish.width !== size.width || v.finish.height !== size.height) {
      ctx.addIssue({ code: "custom", message: "La définition ne correspond pas au format" })
    }
  })
export type CreateVideoInput = z.infer<typeof createVideoSchema>

/** L'échec rendu par un worker */
export const videoErrorSchema = z.object({ error: z.string().min(1).max(2000) })

/** État d'une vidéo : finition = toutes les images calculées, le worker vidéo assemble et habille */
export type VideoStatus = "en_attente" | "en_cours" | "finition" | "termine" | "echoue"
/** Une vidéo active : dans la file ou en calcul ; elle ne se supprime pas, on arrête son calcul */
export const ACTIVE_VIDEO_STATUSES: VideoStatus[] = ["en_attente", "en_cours", "finition"]

/** Événement Mercure d'une vidéo, sur le sujet de son auteur (kind : 'video' le distingue d'un rendu d'image) */
export interface VideoEvent {
  kind: "video"
  id: string
  projectId: string
  /** Pour la notification d'une vidéo d'un autre projet que celui ouvert */
  projectName: string
  status: VideoStatus | "supprime"
  position?: number
  framesDone?: number
  frameCount?: number
  error?: string
}

/** Une vidéo telle que la liste et les fenêtres la montrent (sans fichier) */
export interface VideoJobView {
  id: string
  kind: VideoKind
  quality: VideoQuality | null
  status: VideoStatus
  format: VideoFormat
  frameCount: number
  framesDone: number
  /** Durée de la vidéo, en secondes */
  durationS: number
  /** Rang dans la file de son genre, pour une vidéo en attente */
  position?: number
  createdAt: string
  finishedAt?: string
  /** Poids du MP4, pour une vidéo prête */
  size?: number
  userName?: string
  mine: boolean
  /** Prête, et pas encore regardée par son auteur (qui lit la liste) */
  isNew: boolean
  /** Celui qui lit la liste peut la supprimer, ou arrêter son calcul (son auteur, ou le propriétaire du projet) */
  canDelete: boolean
  error?: string
}
